"use client";

import { imageSize, imageUrl } from "@/features/media/mediaUrl";
import { useMediaContext } from "@/features/media/useMediaContext";
import type { Item, ItemProperties } from "@/types";
import { POIBlocks, POIHeaderImage } from "./POIContent";

/** Three gallery images to a row, so a square crop is what the cell wants. */
const GALLERY_IMAGE_SIZE = imageSize(512, 512);

/**
 * The POI as it goes on paper.
 *
 * The same header image and content blocks as on screen, with two differences,
 * both because paper isn't clickable: the gallery is printed as the pictures
 * themselves rather than as buttons that open a lightbox, and the tenant's
 * "visit the website" button is left out — it renders the field's *label*, so on
 * paper it would be a word with nowhere to go.
 */
export function POIPrintPreview({ item }: { item: Item }) {
  return (
    <div className="relative max-w-full bg-white">
      <POIHeaderImage item={item} />

      <div className="poi-content ml-5 mr-5">
        <POIBlocks item={item} />
      </div>

      <PrintedGallery properties={item.properties ?? {}} />
    </div>
  );
}

/** The POI's gallery photos, laid out as a contact sheet under the details. */
function PrintedGallery({ properties }: { properties: ItemProperties }) {
  const media = useMediaContext();

  const images = (properties.mediasets?.gallery ?? []).flatMap((image) => {
    const url = imageUrl(image, GALLERY_IMAGE_SIZE, media);
    return url ? [{ url, name: image.fileName ?? "" }] : [];
  });

  if (images.length === 0) return null;

  return (
    <div className="ml-5 mr-5 grid grid-cols-3 gap-4">
      {images.map((image) => (
        <div
          key={image.url}
          className="flex h-48 w-full items-center justify-center overflow-hidden rounded-md border shadow-md"
        >
          {/* Tenant-uploaded, arbitrary dimensions, same-origin — and cloned into
              a print iframe, which next/image's loader would not survive. */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={image.url}
            alt={image.name}
            className="max-h-full max-w-full object-contain p-1"
          />
        </div>
      ))}
    </div>
  );
}
